import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { getApiHeaders } from '../utils/apiHelpers';
import {
  Home, ShoppingCart, Package, Receipt, ShieldCheck, FileText, UtensilsCrossed,
  LogOut, Menu, X, User, Bell, Settings, Truck, ChevronLeft, RefreshCw,
  Tag, Thermometer, Sparkles, Droplets, BarChart3, DollarSign, Wine, Users, ChefHat
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import NotificationBell from '../components/NotificationBell';
import NotificationPrompt from '../components/NotificationPrompt';
import ScadenzeWhatsAppDialog from '../components/ScadenzeWhatsAppDialog';

// Import pagine esistenti
import OrdiniPage from './OrdiniPage';
import HACCPPage from './HACCPPage';
import DDTPage from './DDTPage';
import FBCostPage from './FBCostPage';
import MenuPage from './MenuPage';
import GestioneBollePage from './GestioneBollePage';
import MagazzinoPage from './MagazzinoPage';
import ListaViniPage from './ListaViniPage';
import TeamManagementPage from './TeamManagementPage';
import TenantFattureElettroniche from '../components/tenant/TenantFattureElettroniche';
import TenantMobileHome from './TenantMobileHome';
import TenantMobileSpese from '../components/tenant/TenantMobileSpese';
import TenantMobileMagazzino from '../components/tenant/TenantMobileMagazzino';
import TenantMobileFatture from '../components/tenant/TenantMobileFatture';
import TenantMobileOrdini from '../components/tenant/TenantMobileOrdini';
import TenantMobileDDT from '../components/tenant/TenantMobileDDT';
import TenantMobileHACCP from '../components/tenant/TenantMobileHACCP';
import TenantMobileFBCost from '../components/tenant/TenantMobileFBCost';
import CostiRicaviSection from '../components/CostiRicaviSection';
import TenantMobileMenu from '../components/tenant/TenantMobileMenu';
import TenantMobileBolle from '../components/tenant/TenantMobileBolle';
import { API } from '../utils/api';

// Labels per ruoli team (per display)
const TEAM_ROLE_LABELS = {
  executive_chef: "Executive Chef",
  sous_chef: "Sous Chef", 
  chef_de_partie: "Chef de Partie",
  commis: "Commis",
  pasticcere: "Pasticcere",
  pizzaiolo: "Pizzaiolo",
  maitre: "Maître",
  sommelier: "Sommelier",
  cameriere: "Cameriere",
  barman: "Barman",
  hostess: "Hostess",
  magazziniere: "Magazziniere",
  cassiere: "Cassiere",
  custom: "Personalizzato"
};

// Funzione per ottenere l'icona appropriata per il ruolo
const getRoleIcon = (teamRole, isAdmin, isManager) => {
  if (isAdmin) return { icon: ShieldCheck, color: 'from-emerald-500 to-emerald-600' };
  if (isManager) return { icon: User, color: 'from-blue-500 to-blue-600' };
  
  const roleIcons = {
    executive_chef: { icon: ChefHat, color: 'from-orange-500 to-red-500' },
    sous_chef: { icon: ChefHat, color: 'from-orange-400 to-orange-600' },
    chef_de_partie: { icon: ChefHat, color: 'from-amber-500 to-orange-500' },
    commis: { icon: UtensilsCrossed, color: 'from-amber-400 to-amber-600' },
    pasticcere: { icon: ChefHat, color: 'from-pink-400 to-pink-600' },
    pizzaiolo: { icon: ChefHat, color: 'from-red-400 to-red-600' },
    maitre: { icon: User, color: 'from-purple-500 to-purple-600' },
    sommelier: { icon: Wine, color: 'from-red-700 to-red-900' },
    cameriere: { icon: User, color: 'from-cyan-500 to-cyan-600' },
    barman: { icon: Wine, color: 'from-amber-500 to-amber-700' },
    hostess: { icon: User, color: 'from-pink-500 to-pink-600' },
    magazziniere: { icon: Package, color: 'from-slate-500 to-slate-600' },
    cassiere: { icon: Receipt, color: 'from-green-500 to-green-600' },
  };
  
  return roleIcons[teamRole] || { icon: User, color: 'from-cyan-500 to-cyan-600' };
};

// Hook per rilevare mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
};

const TenantMainApp = ({ user, tenantDb, onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [currentTenant, setCurrentTenant] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentPage, setCurrentPageState] = useState('home');
  const [pageParams, setPageParams] = useState(null);
  const [showScadenzeWA, setShowScadenzeWA] = useState(false);
  const [stats, setStats] = useState(null);
  const [showNotifPrompt, setShowNotifPrompt] = useState(false);
  const tenantPushTag = (tenantDb || localStorage.getItem('tenant_db') || '').replace(/^(tenant_)+/, '');
  const isMobile = useIsMobile();
  
  // Mostra il prompt attivazione notifiche push dopo il login (gestisce da solo iOS/già attive/già rifiutato)
  useEffect(() => {
    const t = setTimeout(() => setShowNotifPrompt(true), 3000);
    return () => clearTimeout(t);
  }, []);
  
  // Wrapper per setCurrentPage che accetta parametri opzionali
  const setCurrentPage = (page, params = null) => {
    if (page === 'scadenze_whatsapp') {
      setShowScadenzeWA(true);
      return;
    }
    setCurrentPageState(page);
    setPageParams(params);
  };

  // Deep-link da click su notifica push (OneSignal): naviga alla sezione indicata
  useEffect(() => {
    const goTo = (det) => {
      if (det?.page === 'scadenze_whatsapp') {
        setShowScadenzeWA(true);
      } else if (det?.page) {
        setCurrentPageState(det.page);
        setPageParams(det.sezione ? { sezione: det.sezione } : null);
      }
    };
    try {
      const raw = localStorage.getItem('push_nav');
      if (raw) {
        localStorage.removeItem('push_nav');
        const det = JSON.parse(raw);
        if (det.ts && Date.now() - det.ts < 5 * 60 * 1000) goTo(det);
      }
    } catch (e) { /* noop */ }
    const handler = (e) => goTo(e.detail);
    window.addEventListener('push-navigate', handler);
    return () => window.removeEventListener('push-navigate', handler);
  }, []);
  
  const [enabledSections, setEnabledSections] = useState({
    ordini: true, ddt: true, haccp: true, fb_cost: true, menu: true,
    spese: true, magazzino: true, fatture: true, team: true
  });

  // Helper per ottenere headers con tenant DB - usa getApiHeaders centralizzato
  const getHeaders = useCallback(() => {
    return getApiHeaders();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const tenantList = JSON.parse(localStorage.getItem('tenant_list') || '[]');
        if (tenantList.length > 0) {
          let defaultTenant = tenantList.find(t => t.is_default) || tenantList[0];
          
          // Normalizza tenant_id (può essere salvato come id o tenant_id)
          const tenantId = defaultTenant.tenant_id || defaultTenant.id;
          
          // Se manca il nome del tenant, prova a recuperarlo dall'API
          if (!defaultTenant.nome && !defaultTenant.tenant_name && tenantId) {
            try {
              const tenantInfo = await axios.get(`${API}/saas/tenants/${tenantId}/info`, { 
                headers: getHeaders() 
              }).catch(() => null);
              if (tenantInfo?.data?.nome) {
                defaultTenant = { 
                  ...defaultTenant, 
                  nome: tenantInfo.data.nome, 
                  tenant_name: tenantInfo.data.nome,
                  tenant_id: tenantId  // Assicurati che tenant_id sia presente
                };
                // Aggiorna localStorage con il nome
                const updatedList = tenantList.map(t => 
                  (t.tenant_id || t.id) === tenantId ? defaultTenant : t
                );
                localStorage.setItem('tenant_list', JSON.stringify(updatedList));
              }
            } catch (e) {
              console.log('Non riesco a recuperare info tenant:', e);
            }
          }
          
          setCurrentTenant(defaultTenant);
          
          // ===== SINCRONIZZA TAG ONESIGNAL PER NOTIFICHE =====
          // Imposta il tag tenant_id su OneSignal per ricevere notifiche specifiche
          // Usa database_name (es: il_ristorante_di_porta_pia_b0c50cc9) che corrisponde
          // a quello usato dal backend per filtrare le notifiche
          const dbName = defaultTenant.database_name || defaultTenant.db_name || tenantId;
          // Rimuovi il prefisso "tenant_" se presente per uniformità
          const tenantTag = (dbName || '').replace(/^(tenant_)+/, '');
          
          if (tenantTag) {
            // OneSignalDeferred garantisce l'esecuzione DOPO OneSignal.init (evita race al caricamento)
            window.OneSignalDeferred = window.OneSignalDeferred || [];
            window.OneSignalDeferred.push(async function(OneSignal) {
              try {
                // Auto-riparazione: permesso concesso ma iscrizione disattiva (tipico iOS) -> riattiva
                if (OneSignal.Notifications?.permission && OneSignal.User?.PushSubscription?.optedIn === false) {
                  await OneSignal.User.PushSubscription.optIn();
                  console.log('[OneSignal v16] iscrizione push riattivata (optIn)');
                }
                await OneSignal.User.addTag("tenant_id", tenantTag);
                console.log('[OneSignal v16] Tag tenant_id sincronizzato:', tenantTag, '| sub:', OneSignal.User?.PushSubscription?.id, '| optedIn:', OneSignal.User?.PushSubscription?.optedIn);
              } catch (e) {
                console.log('OneSignal tag sync error:', e);
              }
            });
          }
          // ===================================================
          
          // Se è un team member, usa le sue enabled_sections
          if (defaultTenant.team_role && defaultTenant.enabled_sections) {
            const teamSections = {};
            // Disabilita tutto di default
            Object.keys(enabledSections).forEach(key => {
              teamSections[key] = false;
            });
            // Abilita solo le sezioni assegnate al team member
            defaultTenant.enabled_sections.forEach(section => {
              teamSections[section] = true;
            });
            setEnabledSections(teamSections);
            setLoading(false);
            return; // Non caricare altre sezioni per team members
          }
        }
        
        // Load stats (solo per admin/manager)
        try {
          const response = await axios.get(`${API}/tenant/stats`, { headers: getHeaders() });
          setStats(response.data);
        } catch (e) {
          console.log('Stats not available');
        }
        
        // Load enabled sections from tenant config (solo per admin/manager)
        try {
          const sectionsResponse = await axios.get(`${API}/gestione/tenant/sections`, { headers: getHeaders() });
          if (sectionsResponse.data?.sections) {
            setEnabledSections(sectionsResponse.data.sections);
          }
        } catch (e) {
          console.log('Sections config not available, using defaults');
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [getHeaders]);

  const handleLogout = () => {
    toast.success('Logout effettuato');
    onLogout();
  };

  // Menu items - definizione base
  const allMenuItems = [
    { id: 'home', icon: Home, label: 'Home', color: 'text-blue-500', sectionKey: null }, // Home sempre visibile
    { id: 'ordini', icon: ShoppingCart, label: 'Ordini', color: 'text-orange-500', sectionKey: 'ordini' },
    { id: 'ddt', icon: Truck, label: 'DDT/Bolle', color: 'text-purple-500', sectionKey: 'ddt' },
    { id: 'haccp', icon: ShieldCheck, label: 'HACCP', color: 'text-teal-500', sectionKey: 'haccp' },
    { id: 'fatture', icon: Receipt, label: 'Fatture', color: 'text-cyan-500', sectionKey: 'fatture' },
    { id: 'fbcost', icon: DollarSign, label: 'F&B Cost', color: 'text-green-500', sectionKey: 'fb_cost' },
    { id: 'menu', icon: UtensilsCrossed, label: 'Menu', color: 'text-red-500', sectionKey: 'menu' },
    { id: 'bolle', icon: FileText, label: 'Gestione Bolle', color: 'text-indigo-500', sectionKey: 'ddt' }, // Collegato a DDT
    { id: 'spese', icon: Receipt, label: 'Costi/Ricavi', color: 'text-pink-500', sectionKey: 'spese' },
    { id: 'magazzino', icon: Package, label: 'Magazzino', color: 'text-amber-500', sectionKey: 'magazzino' },
    { id: 'lista-vini', icon: Wine, label: 'Lista Vini', color: 'text-red-700', sectionKey: 'magazzino' },
    { id: 'team', icon: Users, label: 'Team', color: 'text-violet-500', sectionKey: 'team', adminOnly: true },
  ];

  // Filtra i menu items in base alle sezioni abilitate e ruolo utente
  const userRole = currentTenant?.role || user?.tenants?.[0]?.role || 'operator';
  const isTeamMember = currentTenant?.team_role != null;
  const isAdminOrManager = ['admin', 'manager'].includes(userRole) && !isTeamMember;
  
  const menuItems = allMenuItems.filter(item => {
    if (item.sectionKey === null) return true; // Home sempre visibile
    if (item.adminOnly && !isAdminOrManager) return false; // Solo admin/manager
    return enabledSections[item.sectionKey] === true; // Mostra SOLO se esplicitamente abilitato
  });

  const userRoleLabel = isTeamMember 
    ? (currentTenant?.custom_role_name || TEAM_ROLE_LABELS[currentTenant?.team_role] || 'Team')
    : currentTenant?.role === 'admin' ? 'Admin Ristorante' 
    : currentTenant?.role === 'manager' ? 'Manager' 
    : 'Operatore';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  // Su mobile, mostra versione mobile delle pagine
  if (isMobile) {
    const goHome = () => setCurrentPage('home');
    
    // Render mobile pages
    const renderMobilePage = () => {
      switch (currentPage) {
        case 'home':
          return (
            <TenantMobileHome 
              user={user} 
              setCurrentPage={setCurrentPage} 
              menuItems={menuItems} 
              onLogout={handleLogout}
            />
          );
        case 'spese':
          return <TenantMobileSpese onBack={goHome} getHeaders={getHeaders} />;
        case 'magazzino':
          return <TenantMobileMagazzino onBack={goHome} getHeaders={getHeaders} />;
        case 'fatture':
          return <TenantMobileFatture onBack={goHome} tenantDb={tenantDb} />;
        case 'ordini':
          return <TenantMobileOrdini onBack={goHome} setCurrentPage={setCurrentPage} />;
        case 'ddt':
          return <TenantMobileDDT onBack={goHome} setCurrentPage={setCurrentPage} />;
        case 'haccp':
          return <TenantMobileHACCP onBack={goHome} initialSection={pageParams?.sezione} />;
        case 'fbcost':
          return <TenantMobileFBCost onBack={goHome} />;
        case 'menu':
          return <TenantMobileMenu onBack={goHome} />;
        case 'bolle':
          return <TenantMobileBolle onBack={goHome} openNuovaBolla={pageParams?.openNuovaBolla} />;
        case 'lista-vini':
          return <ListaViniPage onBack={goHome} />;
        case 'team':
          return <TeamManagementPage tenantId={currentTenant?.tenant_id || currentTenant?.id} onBack={goHome} />;
        case 'spese':
          return <CostiRicaviSection onBack={goHome} />;
        default:
          return (
            <TenantMobileHome 
              user={user} 
              setCurrentPage={setCurrentPage} 
              menuItems={menuItems} 
              onLogout={handleLogout}
            />
          );
      }
    };

    return (
      <>
        <Toaster position="top-center" richColors />
        <ScadenzeWhatsAppDialog open={showScadenzeWA} onClose={() => setShowScadenzeWA(false)} />
        {renderMobilePage()}
      </>
    );
  }

  // Render contenuto pagina (Desktop)
  const renderPageContent = () => {
    switch (currentPage) {
      case 'ordini':
        return <OrdiniPage setCurrentPage={setCurrentPage} />;
      case 'haccp':
        return <HACCPPage initialSection={pageParams?.sezione} />;
      case 'ddt':
        return <DDTPage />;
      case 'fatture':
        return <TenantFattureElettroniche tenantDb={tenantDb} />;
      case 'fbcost':
        return <FBCostPage />;
      case 'menu':
        return <MenuPage tenantDb={tenantDb} />;
      case 'bolle':
        return <GestioneBollePage openNuovaBolla={pageParams?.openNuovaBolla} />;
      case 'spese':
        return <CostiRicaviSection />;
      case 'magazzino':
        return <MagazzinoPage />;
      case 'lista-vini':
        return <ListaViniPage />;
      case 'team':
        return <TeamManagementPage tenantId={currentTenant?.tenant_id || currentTenant?.id} />;
      case 'home':
      default:
        return <HomeSection stats={stats} currentTenant={currentTenant} user={user} setCurrentPage={setCurrentPage} menuItems={menuItems} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      <Toaster position="top-center" richColors />
      <NotificationPrompt show={showNotifPrompt} onClose={() => setShowNotifPrompt(false)} tenantId={tenantPushTag} />
      <ScadenzeWhatsAppDialog open={showScadenzeWA} onClose={() => setShowScadenzeWA(false)} />
      
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-[#0B0C3F] border-b border-cyan-900/30 z-50 flex items-center justify-between px-4">
        <button onClick={() => setSidebarOpen(true)} className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
          <Menu className="h-5 w-5 text-white" />
        </button>
        <img src="/images/logo-ristorantissimo.png" alt="Logo" className="h-10 w-auto" />
        <div className="flex items-center gap-2">
          <NotificationBell onNavigate={(page, params) => setCurrentPage(page, params)} />
          <button onClick={handleLogout} className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
            <LogOut className="h-5 w-5 text-white" />
          </button>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-50" onClick={() => setSidebarOpen(false)}>
          <div className="w-72 h-full bg-[#0B0C3F] p-4 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <img src="/images/logo-ristorantissimo.png" alt="Logo" className="h-12 w-auto" />
              <button onClick={() => setSidebarOpen(false)} className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                <X className="h-4 w-4 text-white" />
              </button>
            </div>
            <nav className="space-y-1">
              {menuItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => { setCurrentPage(item.id); setSidebarOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    currentPage === item.id ? 'bg-cyan-500/20 text-cyan-400' : 'text-gray-400 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <item.icon className={`h-5 w-5 ${currentPage === item.id ? 'text-cyan-400' : item.color}`} />
                  <span className="font-medium">{item.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <div className="hidden lg:flex flex-col w-56 bg-[#0B0C3F] border-r border-cyan-900/30 fixed h-full z-50">
        <div className="p-4 flex justify-center border-b border-cyan-900/30">
          <img src="/images/logo-ristorantissimo.png" alt="Logo" className="w-40 h-auto" />
        </div>
        <div className="p-4 border-b border-cyan-900/30">
          <div className="flex items-center gap-3">
            {(() => {
              const { icon: RoleIcon, color } = getRoleIcon(
                currentTenant?.team_role,
                currentTenant?.role === 'admin',
                currentTenant?.role === 'manager'
              );
              return (
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center`}>
                  <RoleIcon className="h-5 w-5 text-white" />
                </div>
              );
            })()}
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{user?.username || user?.nome}</p>
              <p className="text-xs text-gray-400">{userRoleLabel}</p>
            </div>
          </div>
          {currentTenant && (
            <div className="mt-3 p-2 bg-white/5 rounded-lg">
              <p className="text-xs text-gray-400">Ristorante</p>
              <p className="text-sm text-white font-medium truncate">{currentTenant.nome || currentTenant.tenant_name || 'Ristorante'}</p>
            </div>
          )}
        </div>
        <nav className="flex-1 py-4 overflow-y-auto">
          <div className="space-y-1 px-3">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setCurrentPage(item.id)}
                data-testid={`nav-${item.id}`}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  currentPage === item.id ? 'bg-cyan-500/20 text-cyan-400' : 'text-gray-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <item.icon className={`h-5 w-5 ${currentPage === item.id ? 'text-cyan-400' : item.color}`} />
                <span className="font-medium">{item.label}</span>
              </button>
            ))}
          </div>
        </nav>
        <div className="p-4 border-t border-cyan-900/30">
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/10 transition-all">
            <LogOut className="h-5 w-5" />
            <span className="font-medium">Esci</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:ml-56 pt-16 lg:pt-0 min-h-screen pb-20 lg:pb-0">
        {/* Header Desktop con Notifiche in alto a destra */}
        <div className="hidden lg:flex items-center justify-end p-4 border-b border-gray-200 bg-white sticky top-0 z-40">
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">
              {currentTenant?.nome || currentTenant?.tenant_name || 'Ristorante'}
            </span>
            <NotificationBell onNavigate={(page, params) => setCurrentPage(page, params)} darkMode={false} />
          </div>
        </div>
        
        <div className="p-4 lg:p-6">
          {/* Back button for sub-pages */}
          {currentPage !== 'home' && (
            <button 
              onClick={() => setCurrentPage('home')}
              className="mb-4 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
              <span>Torna alla Home</span>
            </button>
          )}
          
          {renderPageContent()}
        </div>
      </div>
    </div>
  );
};

// ============ HOME SECTION ============
const HomeSection = ({ stats, currentTenant, user, setCurrentPage, menuItems }) => {
  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Ciao, {user?.nome || user?.username}!
        </h1>
        <p className="text-gray-500">{currentTenant?.nome || currentTenant?.tenant_name || 'Il tuo ristorante'}</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setCurrentPage('ordini')} data-testid="dashboard-card-ordini">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Ordini</p>
                <p className="text-3xl font-bold text-gray-900">{stats?.ordini?.in_corso || 0}</p>
                <p className="text-xs text-gray-400">{stats?.ordini?.totale || 0} totali</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                <ShoppingCart className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setCurrentPage('bolle')} data-testid="dashboard-card-ddt">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Bolle / DDT</p>
                <p className="text-3xl font-bold text-gray-900">{stats?.ddt?.totale ?? 0}</p>
                <p className="text-xs text-gray-400">registrate</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <Truck className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setCurrentPage('haccp')} data-testid="dashboard-card-haccp">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">HACCP - Conservazione</p>
                <p className={`text-3xl font-bold ${(stats?.haccp?.etichette_scadute || 0) > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                  {stats?.haccp?.etichette_attive ?? 0}
                </p>
                <p className={`text-xs ${(stats?.haccp?.etichette_scadute || 0) > 0 ? 'text-red-500 font-medium' : (stats?.haccp?.etichette_in_scadenza || 0) > 0 ? 'text-amber-600 font-medium' : 'text-gray-400'}`}>
                  {(stats?.haccp?.etichette_scadute || 0) > 0
                    ? `${stats.haccp.etichette_scadute} scadute da smaltire`
                    : (stats?.haccp?.etichette_in_scadenza || 0) > 0
                      ? `${stats.haccp.etichette_in_scadenza} in scadenza`
                      : 'etichette attive'}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center">
                <ShieldCheck className="h-6 w-6 text-teal-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setCurrentPage('fatture')} data-testid="dashboard-card-spese">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Spese Mese</p>
                <p className="text-3xl font-bold text-gray-900">€{(stats?.spese?.mese_corrente || 0).toFixed(0)}</p>
                {(stats?.spese?.anno_corrente || 0) > 0 && (
                  <p className="text-xs text-gray-400">€{(stats.spese.anno_corrente).toFixed(0)} nel {stats?.fatture?.anno || new Date().getFullYear()}</p>
                )}
              </div>
              <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                <Receipt className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions Grid */}
      <Card>
        <CardHeader>
          <CardTitle>Accesso Rapido</CardTitle>
          <CardDescription>Tutte le funzionalità del tuo ristorante</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {menuItems.filter(m => m.id !== 'home').map((item) => (
              <button
                key={item.id}
                onClick={() => setCurrentPage(item.id)}
                className="p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-all text-center group"
              >
                <div className={`w-12 h-12 mx-auto rounded-xl bg-white shadow-sm flex items-center justify-center mb-2 group-hover:scale-110 transition-transform`}>
                  <item.icon className={`h-6 w-6 ${item.color}`} />
                </div>
                <p className="text-sm font-medium text-gray-700">{item.label}</p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Welcome Card */}
      <Card className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <Bell className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-1">Benvenuto in Ristorantissimo!</h3>
              <p className="text-cyan-100 text-sm">
                Hai accesso a tutte le funzionalità: gestione ordini, DDT, HACCP completo,
                F&B Cost, Menu e molto altro. Clicca su una sezione per iniziare.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============ SPESE SECTION (semplificata) ============
const SpeseSection = () => {
  const [spese, setSpese] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totale, setTotale] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSpesa, setNewSpesa] = useState({
    descrizione: '', importo: 0, categoria: 'Materie Prime',
    data: new Date().toISOString().split('T')[0], fornitore: '', metodo_pagamento: 'contanti'
  });
  const [filterCategoria, setFilterCategoria] = useState('all');

  const categorie = ["Materie Prime", "Bevande", "Utenze", "Personale", "Manutenzione", "Marketing", "Altro"];

  const loadSpese = useCallback(async () => {
    try {
      let url = `${API}/gestione/tenant/spese?limit=200`;
      if (filterCategoria !== 'all') url += `&categoria=${filterCategoria}`;
      
      const response = await axios.get(url);
      setSpese(response.data.spese || []);
      setTotale(response.data.totale_importo || 0);
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  }, [filterCategoria]);

  useEffect(() => { loadSpese(); }, [loadSpese]);

  const handleAddSpesa = async () => {
    if (!newSpesa.descrizione || !newSpesa.importo) {
      toast.error('Inserisci descrizione e importo');
      return;
    }
    try {
      await axios.post(`${API}/gestione/tenant/spese`, newSpesa);
      toast.success('Spesa registrata!');
      setShowAddModal(false);
      setNewSpesa({ descrizione: '', importo: 0, categoria: 'Materie Prime', data: new Date().toISOString().split('T')[0], fornitore: '', metodo_pagamento: 'contanti' });
      loadSpese();
    } catch (error) {
      toast.error('Errore nella registrazione');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa spesa?')) return;
    try {
      await axios.delete(`${API}/gestione/tenant/spese/${id}`);
      toast.success('Spesa eliminata');
      loadSpese();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  if (loading) return <div className="flex justify-center p-8"><RefreshCw className="h-8 w-8 animate-spin text-cyan-500" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Gestione Spese</h1>
        <button 
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 flex items-center gap-2"
        >
          + Nuova Spesa
        </button>
      </div>

      <div className="flex gap-3">
        <select 
          value={filterCategoria} 
          onChange={(e) => setFilterCategoria(e.target.value)}
          className="px-4 py-2 border rounded-lg"
        >
          <option value="all">Tutte le categorie</option>
          {categorie.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <Card className="bg-gradient-to-r from-red-500 to-red-600 text-white">
        <CardContent className="p-4">
          <p className="text-red-100 text-sm">Totale Spese</p>
          <p className="text-3xl font-bold">€{totale.toFixed(2)}</p>
        </CardContent>
      </Card>

      <div className="space-y-2">
        {spese.map(s => (
          <Card key={s.id}>
            <CardContent className="p-4 flex justify-between items-center">
              <div>
                <p className="font-medium">{s.descrizione}</p>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Badge variant="outline">{s.categoria}</Badge>
                  <span>{s.data}</span>
                  {s.fornitore && <span>• {s.fornitore}</span>}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-bold text-lg text-red-600">€{s.importo?.toFixed(2)}</span>
                <button onClick={() => handleDelete(s.id)} className="text-red-500 hover:text-red-700">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {spese.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            <Receipt className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>Nessuna spesa registrata</p>
          </CardContent>
        </Card>
      )}

      {/* Modal Nuova Spesa */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Nuova Spesa</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Descrizione *</label>
                <input 
                  type="text"
                  value={newSpesa.descrizione}
                  onChange={e => setNewSpesa({...newSpesa, descrizione: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="es. Acquisto verdure"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Importo € *</label>
                  <input 
                    type="number"
                    step="0.01"
                    value={newSpesa.importo}
                    onChange={e => setNewSpesa({...newSpesa, importo: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Categoria</label>
                  <select 
                    value={newSpesa.categoria}
                    onChange={e => setNewSpesa({...newSpesa, categoria: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    {categorie.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Data</label>
                  <input 
                    type="date"
                    value={newSpesa.data}
                    onChange={e => setNewSpesa({...newSpesa, data: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Fornitore</label>
                  <input 
                    type="text"
                    value={newSpesa.fornitore}
                    onChange={e => setNewSpesa({...newSpesa, fornitore: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Opzionale"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowAddModal(false)} className="px-4 py-2 border rounded-lg">Annulla</button>
              <button onClick={handleAddSpesa} className="px-4 py-2 bg-cyan-500 text-white rounded-lg">Salva</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ MAGAZZINO SECTION (semplificata) ============
const MagazzinoSection = () => {
  const [prodotti, setProdotti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newProdotto, setNewProdotto] = useState({
    nome: '', categoria: '', unita_misura: 'kg', prezzo_acquisto: 0,
    fornitore: '', scorta_minima: 0, quantita: 0
  });

  const loadProdotti = useCallback(async () => {
    try {
      let url = `${API}/gestione/tenant/prodotti?limit=200`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      
      const response = await axios.get(url);
      setProdotti(response.data.prodotti || []);
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { loadProdotti(); }, [loadProdotti]);

  const handleAddProdotto = async () => {
    if (!newProdotto.nome) {
      toast.error('Inserisci il nome del prodotto');
      return;
    }
    try {
      await axios.post(`${API}/gestione/tenant/prodotti`, newProdotto);
      toast.success('Prodotto aggiunto!');
      setShowAddModal(false);
      setNewProdotto({ nome: '', categoria: '', unita_misura: 'kg', prezzo_acquisto: 0, fornitore: '', scorta_minima: 0, quantita: 0 });
      loadProdotti();
    } catch (error) {
      toast.error('Errore nell\'aggiunta');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo prodotto?')) return;
    try {
      await axios.delete(`${API}/gestione/tenant/prodotti/${id}`);
      toast.success('Prodotto eliminato');
      loadProdotti();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  if (loading) return <div className="flex justify-center p-8"><RefreshCw className="h-8 w-8 animate-spin text-cyan-500" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Magazzino</h1>
        <button 
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 flex items-center gap-2"
        >
          + Nuovo Prodotto
        </button>
      </div>

      <div className="flex gap-3">
        <input 
          type="text"
          placeholder="Cerca prodotti..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 px-4 py-2 border rounded-lg"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {prodotti.map(p => (
          <Card key={p.id} className={p.quantita <= p.scorta_minima ? 'border-red-300 bg-red-50' : ''}>
            <CardContent className="p-4">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="font-semibold text-gray-900">{p.nome}</h3>
                  {p.categoria && <Badge variant="outline" className="mt-1">{p.categoria}</Badge>}
                </div>
                <button onClick={() => handleDelete(p.id)} className="text-red-500 hover:text-red-700">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Quantità:</span>
                  <span className={`font-medium ${p.quantita <= p.scorta_minima ? 'text-red-600' : ''}`}>
                    {p.quantita} {p.unita_misura}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Prezzo:</span>
                  <span>€{p.prezzo_acquisto?.toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {prodotti.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            <Package className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>Nessun prodotto trovato</p>
          </CardContent>
        </Card>
      )}

      {/* Modal Nuovo Prodotto */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Nuovo Prodotto</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nome *</label>
                <input 
                  type="text"
                  value={newProdotto.nome}
                  onChange={e => setNewProdotto({...newProdotto, nome: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Categoria</label>
                  <input 
                    type="text"
                    value={newProdotto.categoria}
                    onChange={e => setNewProdotto({...newProdotto, categoria: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="es. Bevande"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Unità</label>
                  <select 
                    value={newProdotto.unita_misura}
                    onChange={e => setNewProdotto({...newProdotto, unita_misura: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="kg">kg</option>
                    <option value="lt">lt</option>
                    <option value="pz">pz</option>
                    <option value="ct">ct</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Prezzo €</label>
                  <input 
                    type="number"
                    step="0.01"
                    value={newProdotto.prezzo_acquisto}
                    onChange={e => setNewProdotto({...newProdotto, prezzo_acquisto: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Quantità</label>
                  <input 
                    type="number"
                    value={newProdotto.quantita}
                    onChange={e => setNewProdotto({...newProdotto, quantita: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowAddModal(false)} className="px-4 py-2 border rounded-lg">Annulla</button>
              <button onClick={handleAddProdotto} className="px-4 py-2 bg-cyan-500 text-white rounded-lg">Salva</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantMainApp;
